import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Box,
  Chip,
  Typography,
  ListSubheader,
} from '@mui/material';
import { ApiEndpoint } from '../../../../types/admin';

interface EndpointSelectorProps {
  endpoints?: ApiEndpoint[];
  groupedEndpoints?: Record<string, ApiEndpoint[]>;
  selectedEndpoint: ApiEndpoint | null;
  onEndpointSelect: (endpoint: ApiEndpoint | null) => void;
  disabled?: boolean;
}

export const EndpointSelector: React.FC<EndpointSelectorProps> = ({
  endpoints,
  groupedEndpoints,
  selectedEndpoint,
  onEndpointSelect,
  disabled = false,
}) => {
  // Create a flat list of all endpoints for searching
  const allEndpoints = React.useMemo(() => {
    if (endpoints) {
      return endpoints;
    }
    if (groupedEndpoints) {
      return Object.values(groupedEndpoints).flat();
    }
    return [];
  }, [endpoints, groupedEndpoints]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const endpointId = event.target.value;
    if (endpointId === '') {
      onEndpointSelect(null);
    } else {
      const endpoint = allEndpoints.find(e => e.id === endpointId);
      onEndpointSelect(endpoint || null);
    }
  };

  return (
    <FormControl fullWidth disabled={disabled}>
      <InputLabel id="endpoint-select-label">Select API Endpoint</InputLabel>
      <Select
        labelId="endpoint-select-label"
        id="endpoint-select"
        value={selectedEndpoint?.id || ''}
        label="Select API Endpoint"
        onChange={handleChange}
      >
        <MenuItem value="">
          <em>--Select Endpoint--</em>
        </MenuItem>
        {groupedEndpoints ? (
          // Render grouped endpoints with headers
          Object.entries(groupedEndpoints).map(([groupName, groupEndpoints]) => [
            <ListSubheader key={`header-${groupName}`} sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}>
              {groupName}
            </ListSubheader>,
            ...groupEndpoints.map((endpoint) => (
              <MenuItem key={endpoint.id} value={endpoint.id} disabled={!endpoint.enabled}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Typography variant="body2" sx={{ width: 200, flexShrink: 0, pl: 2 }}>
                    {endpoint.name}
                  </Typography>
                  <Chip
                    label={endpoint.method}
                    size="small"
                    color={endpoint.method === 'GET' ? 'primary' : 'secondary'}
                    sx={{ minWidth: 60 }}
                  />
                  <Typography variant="body2" sx={{ flexGrow: 1, ml: 1 }}>
                    {endpoint.path}
                  </Typography>
                  {!endpoint.enabled && (
                    <Chip label="Disabled" size="small" variant="outlined" />
                  )}
                </Box>
              </MenuItem>
            ))
          ]).flat()
        ) : (
          // Render flat endpoints (fallback)
          allEndpoints.map((endpoint) => (
            <MenuItem key={endpoint.id} value={endpoint.id} disabled={!endpoint.enabled}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography variant="body2" sx={{ width: 200, flexShrink: 0 }}>
                  {endpoint.name}
                </Typography>
                <Chip
                  label={endpoint.method}
                  size="small"
                  color={endpoint.method === 'GET' ? 'primary' : 'secondary'}
                  sx={{ minWidth: 60 }}
                />
                <Typography variant="body2" sx={{ flexGrow: 1, ml: 1 }}>
                  {endpoint.path}
                </Typography>
                {!endpoint.enabled && (
                  <Chip label="Disabled" size="small" variant="outlined" />
                )}
              </Box>
            </MenuItem>
          ))
        )}
      </Select>
      {selectedEndpoint && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {selectedEndpoint.description}
          </Typography>
        </Box>
      )}
    </FormControl>
  );
};